import { useEffect, useRef, useState } from "react";
import { startOfWeek, addDays, format, isSameDay } from "date-fns";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/solid";

export default function WeeklyPlannerModal({
  isOpen,
  onClose,
  recipeId,
  onAdded,
}) {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [plannedDates, setPlannedDates] = useState([]);
  const modalRef = useRef(null);

  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const fetchPlannedDates = async () => {
    try {
      const isoDate = format(weekStart, "yyyy-MM-dd");
      const res = await apiClient.get(`/calendar/week/${isoDate}`);
      const normalized = res.data.map(entry => format(new Date(entry.date), "yyyy-MM-dd"));
      setPlannedDates(normalized);
    } catch (err) {
      console.error("Failed to fetch planned dates:", err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPlannedDates();
    }
  }, [isOpen, weekStart]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const addToDate = async (date) => {
    try {
      const formatted = format(date, "yyyy-MM-dd");
      await apiClient.post("/calendar", {
        recipeId,
        date: formatted,
      });
      toast.success("Recipe added to calendar!");
      if (onAdded) onAdded();
      onClose();
    } catch (err) {
      toast.error("Failed to add recipe to calendar.");
      console.error(err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 bg-black bg-opacity-30 flex justify-center items-center px-4">
      <div ref={modalRef} className="bg-white rounded-lg shadow-lg w-full max-w-md p-4 pt-8 relative">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-500 hover:text-black">
            <XMarkIcon className="h-6 w-6" />
        </button>
        <div className="flex justify-between items-center mb-4">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeftIcon className="h-6 w-6" /></button>
          <h2 className="text-lg font-semibold">Week of {format(weekStart, 'do MMM')}</h2>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))}><ChevronRightIcon className="h-6 w-6" /></button>
        </div>

        <div className="grid grid-cols-7 gap-2">
          {weekDates.map((date) => {
            const isToday = isSameDay(date, new Date());
            const hasMeal = plannedDates.includes(format(date, "yyyy-MM-dd"));

            return (
              <button
                key={date.toISOString()}
                onClick={() => addToDate(date)}
                className={`flex flex-col items-center justify-start p-2 rounded text-sm border transition min-w-[40px] min-h-16
                  ${isToday ? 'bg-sage text-white font-semibold' : 'bg-white'}
                  hover:bg-sage hover:text-white`
                }
              >
                <div className="text-xs font-medium">{format(date, 'EE')}</div>
                <div className="text-xs">{format(date, 'd')}</div>
                {hasMeal && <div className="text-sm mt-1">🍴</div>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
} 
